import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { PlusCircle, Edit, Trash2, LogOut } from 'lucide-react';

const AdminDashboard = () => {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
    fetchPosts();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin');
      return;
    }

    try {
      const response = await fetch('/api/auth.php', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        localStorage.removeItem('admin_token');
        navigate('/admin');
      }
    } catch (err) {
      navigate('/admin');
    }
  };

  const fetchPosts = async () => {
    try {
      const response = await fetch('/api/posts.php');
      const data = await response.json();
      setPosts(data);
    } catch (err) {
      console.error('Failed to fetch posts:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    const token = localStorage.getItem('admin_token');
    try {
      await fetch('/api/auth.php', {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
    } catch (err) {
      console.error('Logout error:', err);
    }
    localStorage.removeItem('admin_token');
    navigate('/admin');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this post?')) {
      return;
    }

    const token = localStorage.getItem('admin_token');
    try {
      await fetch(`/api/posts.php?id=${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      fetchPosts();
    } catch (err) {
      alert('Failed to delete post');
    }
  };

  return (
    <div className="container mx-auto px-4 pt-32 pb-16">
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h1 className={`text-4xl font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Blog Dashboard
          </h1>
          <div className="flex gap-4">
            <button
              onClick={() => navigate('/admin/create')}
              className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
                isDarkMode
                  ? 'bg-white/90 text-black hover:bg-white'
                  : 'bg-gray-900/90 text-white hover:bg-gray-900'
              }`}
            >
              <PlusCircle className="w-5 h-5" />
              New Post
            </button>
            <button
              onClick={handleLogout}
              className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
                isDarkMode
                  ? 'bg-red-500/20 text-red-200 hover:bg-red-500/30 border border-red-500/50'
                  : 'bg-red-100/50 text-red-700 hover:bg-red-100 border border-red-200/50'
              }`}
            >
              <LogOut className="w-5 h-5" />
              Logout
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <p className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>Loading posts...</p>
          </div>
        ) : (
          <div className="grid gap-6">
            {posts.map((post) => (
              <div
                key={post.id}
                className={`p-6 rounded-2xl backdrop-blur-md shadow-lg border ${
                  isDarkMode
                    ? 'bg-gray-800/30 border-gray-700/30'
                    : 'bg-white/30 border-gray-200/30'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h2 className={`text-2xl font-semibold mb-2 ${
                      isDarkMode ? 'text-white' : 'text-gray-900'
                    }`}>
                      {post.title}
                    </h2>
                    <p className={`text-sm mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {post.date} · {post.readTime} min read
                    </p>
                    <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      {post.excerpt}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {post.tags.map((tag) => (
                        <span
                          key={tag}
                          className={`px-3 py-1 text-xs font-medium rounded-full backdrop-blur-sm ${
                            isDarkMode
                              ? 'bg-gray-700/30 text-gray-200 border border-gray-600/30'
                              : 'bg-gray-100/30 text-gray-800 border border-gray-200/30'
                          }`}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="flex gap-2 ml-4">
                    <button
                      onClick={() => navigate(`/admin/edit/${post.id}`)}
                      className={`p-2 rounded-lg transition-colors ${
                        isDarkMode
                          ? 'hover:bg-gray-700/50 text-blue-400'
                          : 'hover:bg-gray-200/50 text-blue-600'
                      }`}
                    >
                      <Edit className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleDelete(post.id)}
                      className={`p-2 rounded-lg transition-colors ${
                        isDarkMode
                          ? 'hover:bg-red-500/20 text-red-400'
                          : 'hover:bg-red-100/50 text-red-600'
                      }`}
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
